/**
 * Mobile board camera (10-presentation-and-ux.md): two fixed zoom levels --
 * "readable" (board sized to the stage height, horizontally pannable) and
 * "overview" (whole board fits). Pinch or double-tap toggles; one-finger drag
 * pans. Desktop never activates this: the sizing rule in styles.css already
 * fits the board.
 *
 * Implemented purely with CSS width + translateX, so getBoundingClientRect()
 * keeps input coordinate mapping correct with zero extra math.
 */
export type ZoomMode = 'readable' | 'overview';

const ASPECT = 1120 / 640;

export class Camera {
  mode: ZoomMode = 'readable';
  private tx = 0;

  constructor(
    private readonly stage: HTMLElement,
    private readonly canvas: HTMLCanvasElement,
  ) {}

  /** Recompute sizing for the current stage box; call on activate + resize. */
  layout(): void {
    const box = this.stage.getBoundingClientRect();
    // "readable": board fills the stage height, horizontal pan covers the rest.
    // "overview": the whole board fits inside the stage.
    const w =
      this.mode === 'readable' ? box.height * ASPECT : Math.min(box.width, box.height * ASPECT);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.maxWidth = 'none';
    // Center the board when it fits; pin left (pannable) when it overflows.
    this.canvas.style.margin = w <= box.width ? '0 auto' : '0';
    this.pan(0, 0); // re-clamp
  }

  pan(dx: number, _dy: number): void {
    const box = this.stage.getBoundingClientRect();
    const w = this.canvas.getBoundingClientRect().width;
    const min = Math.min(0, box.width - w);
    this.tx = Math.max(min, Math.min(0, this.tx + dx));
    this.canvas.style.transform = `translateX(${this.tx}px)`;
  }

  toggleZoom(): void {
    this.mode = this.mode === 'readable' ? 'overview' : 'readable';
    this.tx = 0;
    this.layout();
  }

  setMode(mode: ZoomMode): void {
    if (this.mode !== mode) this.toggleZoom();
  }
}
